import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WorkspaceShell } from "./WorkspaceShell";
import {
  ScheduleTab, MatchmakingTab, FiresidePanelistsTab, ExecutiveUpdatesTab,
  HomepageContentTab, EventConfigTab, AnnouncementsTab,
} from "@/pages/Admin";
import { LayoutDashboard, Settings } from "lucide-react";

/**
 * Event Operations Workspace — schedule, matchmaking, homepage & content, event configuration.
 */
export default function EventOpsWorkspace() {
  const [sessions, setSessions] = useState([]);
  const [exhibitors, setExhibitors] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    api.get("/sessions").then(({ data }) => setSessions(data.sessions || [])).catch(() => {});
    api.get("/admin/exhibitors").then(({ data }) => setExhibitors(data.exhibitors || [])).catch(() => {});
    api.get("/announcements").then(({ data }) => setAnnouncements(data.announcements || [])).catch(() => {});
    api.get("/admin/procurement/meetings").then(({ data }) => setMeetings(data.meetings || [])).catch(() => {});
  }, []);

  const tabs = [
    {
      value: "schedule", label: "Schedule",
      render: () => <ScheduleTab list={sessions}
        onAdd={(s) => setSessions([...sessions, s])}
        onRemove={(id) => setSessions(sessions.filter((x) => x.id !== id))}
        onUpdate={(s) => setSessions(sessions.map((x) => x.id === s.id ? s : x))} />,
    },
    { value: "speakers", label: "Speakers", render: () => <FiresidePanelistsTab /> },
    { value: "matchmaking", label: "Matchmaking", render: () => <MatchmakingTab exhibitors={exhibitors} /> },
    {
      value: "meetings", label: "Meeting Requests",
      render: () => (
        <div className="card-flat p-6" data-testid="eo-meetings">
          {meetings.length === 0 && <p className="text-sm text-zinc-500">No meeting requests yet.</p>}
          <div className="grid gap-3">
            {meetings.map((m) => (
              <div key={m.id} className="border-b border-zinc-100 last:border-0 py-3">
                <div className="text-sm font-medium">{m.company_name} <span className="text-zinc-500">· {m.contact_name}</span></div>
                <div className="text-xs text-zinc-500">{m.email}</div>
                <div className="text-xs text-[#A39171] mt-1">{(m.interest_categories || []).join(" · ")}</div>
                {m.notes && <p className="text-sm text-zinc-600 mt-1">{m.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      value: "announcements", label: "Announcements",
      render: () => <AnnouncementsTab list={announcements}
        onAdd={(a) => setAnnouncements([a, ...announcements])}
        onUpdate={(a) => setAnnouncements(announcements.map((x) => x.id === a.id ? a : x))}
        onRemove={(id) => setAnnouncements(announcements.filter((x) => x.id !== id))} />,
    },
    { value: "updates", label: "Executive Updates", render: () => <ExecutiveUpdatesTab /> },
    { value: "homepage", label: "Homepage Content", render: () => <HomepageContentTab exhibitors={exhibitors} /> },
    { value: "event", label: "Event Config", render: () => <EventConfigTab /> },
  ];

  return (
    <WorkspaceShell
      title="Event Operations"
      subtitle="Schedule, speakers, matchmaking, content, and event configuration."
      tabs={tabs}
      dataTestId="ws-title-event-ops"
      headerActions={
        <>
          <Link to="/command-center" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="eo-link-command-center">
            <LayoutDashboard size={12} /> Command Center
          </Link>
          <Link to="/event-settings" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="eo-link-event-settings">
            <Settings size={12} /> Event Settings
          </Link>
        </>
      }
    />
  );
}
